/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import { Description } from "@mui/icons-material";
import { CaseWhenBenefit, ReimbursementRequest } from "services/GraphQl";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import moment from "moment";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref as storageRef } from "firebase/storage";
import { useFirebase } from "contexts/Firebase";

interface ProcessedReimbursementRequestsTableProps {
  whenBenefit: CaseWhenBenefit;
}

const ProcessedReimbursementsTable: React.FC<ProcessedReimbursementRequestsTableProps> = ({ whenBenefit }) => {
  const reimbursementRequests = whenBenefit.reimbursementRequests || [];
  const { storage } = useFirebase();
  
  // Filter for processed requests (approved or denied)
  const processedRequests = reimbursementRequests.filter(
    (request) => request.status === "REIMBURSED" || request.status === "DENIED"
  );

  const formatMonth = (monthStr: string) => {
    return moment(monthStr, "YYYY-MM").format("MMMM YYYY");
  };

  const formatMonths = (coverageMonths: string[]) => {
    if (!coverageMonths || coverageMonths.length === 0) return "—";
    
    if (coverageMonths.length === 1) {
      return formatMonth(coverageMonths[0]);
    }
    
    // For multiple months, show range or list
    const sortedMonths = [...coverageMonths].sort();
    if (coverageMonths.length <= 3) {
      return sortedMonths.map(formatMonth).join(", ");
    }
    
    return `${formatMonth(sortedMonths[0])} - ${formatMonth(sortedMonths[sortedMonths.length - 1])}`;
  };

  const formatDate = (date: string | Date | moment.Moment | null | undefined) => {
    if (!date) return "—";
    return moment(date).format("MMM DD, YYYY");
  };

  // V1 approach - component for each file
  const DownloadFile = ({ file }: { file: { name: string; type: string; path: string } }) => {
    const [downloadURL, loading, error] = useDownloadURL(storageRef(storage, file.path));
    
    if (loading) {
      return <Typography variant="body2">{file.name}</Typography>;
    }
    if (error) {
      return (
        <Typography variant="body2" color="error">
          Error: {file.name}: {error.message}
        </Typography>
      );
    }
    if (!downloadURL) return <Typography variant="body2">{file.name}</Typography>;
    
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Description fontSize="small" color="primary" />
        <Button
          variant="text"
          component="a"
          href={downloadURL}
          target="_blank"
          sx={{ 
            textDecoration: "none",
            fontSize: "0.875rem",
            minWidth: "auto",
            padding: 0,
            color: "primary.main",
            textTransform: "none",
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: "transparent"
            }
          }}
        >
          {file.name}
        </Button>
      </Box>
    );
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case "REIMBURSED":
        return (
          <Chip
            label="✓ Reimbursed"
            size="small"
            sx={{
              backgroundColor: "#e8f5e8",
              color: "#2e7d32",
              fontWeight: "medium",
            }}
          />
        );
      case "DENIED":
        return (
          <Chip
            label="✗ Denied"
            size="small"
            sx={{
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              fontWeight: "medium",
            }}
          />
        );
      default:
        return (
          <Chip
            label={status}
            size="small"
            variant="outlined"
          />
        );
    }
  };

  const renderAttachments = (files: any[]) => {
    if (!files || files.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {files.map((file, index) => (
          <DownloadFile key={index} file={file} />
        ))}
      </Box>
    );
  };

  if (processedRequests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Processed Reimbursement Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No processed reimbursement requests
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
        Processed Reimbursement Requests
      </Typography>

      <TableContainer component={Paper} sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Month(s)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Amount Requested
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Amount Paid
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Date Processed
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Notes
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Attachments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedRequests.map((request: ReimbursementRequest) => {
              return (
                <TableRow key={request.reimbursementRequestId} sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}>
                  <TableCell>
                    <Typography variant="body2">
                      {formatMonths(request.coverageMonths)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormattedCurrency value={request.amount} />
                  </TableCell>
                  <TableCell>
                    <FormattedCurrency value={request.approvedAmount || 0} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(request.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(request.status)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {request.notes}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    {renderAttachments(request.files)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProcessedReimbursementsTable; 